import { ReactElement, useMemo } from 'react';
import { AbsoluteFill, interpolate, useCurrentFrame } from 'remotion';

// Components
import { ScrollingTextBackground } from '@/components/common/ScrollingTextBackground';
import { MainSection } from './components/MainSection';

// Constants
import {
  CLAMP_BOTH,
  SCENE_7_8_BACKGROUND_FADE_DELAY,
  SCENE_7_8_BACKGROUND_FADE_DURATION,
  SCENE_7_8_EXIT_DURATION,
} from '@/constants';

// Types
import { EmployeeTraining, Mentee, Supportee } from '@/types/employee';

// Utils
import {
  getTopSupportersFromTrainings,
  convertMenteesToTeamMembers,
  convertSupporteesToTeamMembers,
} from './utils';
import { employeeYearInReviewTheme } from '../../theme';

interface Scene7Props {
  /** Delay in frames before animations start */
  animationDelay?: number;
  exitStartFrame?: number;
  exitDuration?: number;
  bgFadeDelay?: number;
  bgFadeDur?: number;
  trainings?: EmployeeTraining[];
  mentees?: Mentee[];
  supportees?: Supportee[];
}

export const Scene7 = ({
  animationDelay = 0,
  exitStartFrame = 0,
  exitDuration = SCENE_7_8_EXIT_DURATION,
  bgFadeDelay = SCENE_7_8_BACKGROUND_FADE_DELAY,
  bgFadeDur = SCENE_7_8_BACKGROUND_FADE_DURATION,
  trainings = [],
  mentees = [],
  supportees = [],
}: Scene7Props): ReactElement => {
  const frame = useCurrentFrame();
  const supporters = useMemo(
    () => getTopSupportersFromTrainings(trainings),
    [trainings],
  );

  const menteeMembers = useMemo(
    () => convertMenteesToTeamMembers(mentees),
    [mentees],
  );

  const supporteesMembers = useMemo(
    () => convertSupporteesToTeamMembers(supportees),
    [supportees],
  );

  // Frame at which background fade begins (after exit cards have started)
  const bgFadeStart = exitStartFrame + bgFadeDelay;

  // Fade background from opaque to transparent
  const bgOpacity = interpolate(
    frame,
    [bgFadeStart, bgFadeStart + bgFadeDur],
    [1, 0],
    CLAMP_BOTH,
  );

  return (
    <AbsoluteFill className={employeeYearInReviewTheme.colors.bgSecondary}>
      <AbsoluteFill style={{ opacity: bgOpacity }}>
        <ScrollingTextBackground
          texts={['supporters', 'mentees', 'supportees']}
        />
      </AbsoluteFill>

      <AbsoluteFill className="z-10">
        <MainSection
          supporters={supporters}
          mentees={menteeMembers}
          supportees={supporteesMembers}
          animationDelay={animationDelay}
          exitStartFrame={exitStartFrame}
          exitDuration={exitDuration}
        />
      </AbsoluteFill>
    </AbsoluteFill>
  );
};
